/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ColumnFiltersState, SortingState, VisibilityState } from '@tanstack/react-table'
import { Lead } from 'types/entities/Lead'
import { LeadCategory } from 'types/enums/LeadCategory'
import type { LeadTask } from 'types/entities/LeadTask'
import type { LeadNote } from 'types/entities/LeadNote'
import { mergePresetTagsIntoCatalog } from 'utils/leads/leadFollowUpPresets'

type TableSearchScalar = string | number | boolean | null
type TableSearch =
    | TableSearchScalar
    | TableSearchScalar[]
    | Record<string, TableSearchScalar | TableSearchScalar[]>

interface LeadsTableUiState {
    pageIndex: number
    pageSize: number
    globalFilter: string
    columnFilters: ColumnFiltersState
    sorting: SortingState
    view: LeadCategory | 'all'
    columnVisibility?: VisibilityState
}

interface LeadsTableMetaState {
    currentPage: number
    perPage: number
    total: number
    sortBy: string | null
    sort: 'asc' | 'desc' | null
    search: TableSearch
}

interface LeadsTableState {
    ui: LeadsTableUiState
    meta: LeadsTableMetaState | null
    hasLoaded: boolean
}

export interface LeadsState {
    all: Lead[]
    selectedLead: Lead | null
    table: LeadsTableState
    tagsCatalog: string[]
}

export const DEFAULT_LEADS_TABLE_UI: LeadsTableUiState = {
    pageIndex: 0,
    pageSize: 50,
    globalFilter: '',
    columnFilters: [],
    sorting: [{ id: 'created_at', desc: true }],
    view: 'all',
    columnVisibility: {
        source: false,
        company_type: false,
        company_branche: false,
        company_state: false,
        company_address: false,
        contact_position: false,
        contact_gender: false,
        requested_date: false,
        persons: false,
        potential_value: false,
    },
}

const initialState: LeadsState = {
    all: [],
    selectedLead: null,
    table: {
        ui: { ...DEFAULT_LEADS_TABLE_UI },
        meta: null,
        hasLoaded: false,
    },
    tagsCatalog: mergePresetTagsIntoCatalog([]),
}

const leadsSlice = createSlice({
    name: 'leads',
    initialState,
    reducers: {
        setLeads(state, action: PayloadAction<Lead[]>) {
            state.all = action.payload ?? []
        },
        pushLead(state, action: PayloadAction<Lead>) {
            const lead = action.payload
            const index = state.all.findIndex((l) => l.id === lead.id)
            if (index === -1) {
                state.all.push(lead)
            } else {
                state.all[index] = lead
            }
        },
        pushLeadTask(state, action: PayloadAction<LeadTask>) {
            const leadTask = action.payload
            if (!state.selectedLead) {
                return
            }
            if (!state.selectedLead.tasks) {
                state.selectedLead.tasks = []
            }
            const index = state.selectedLead.tasks.findIndex((task) => task.id === leadTask.id)
            if (index === -1) {
                state.selectedLead.tasks.push(leadTask)
            } else {
                state.selectedLead.tasks[index] = leadTask
            }
        },
        pushLeadNote(state, action: PayloadAction<LeadNote>) {
            const leadNote = action.payload
            if (!state.selectedLead) {
                return
            }
            if (!state.selectedLead.notes) {
                state.selectedLead.notes = [action.payload]
            }
            const index = state.selectedLead?.notes.findIndex((task) => task.id === leadNote.id)

            if (index === -1) {
                state.selectedLead.notes.push(leadNote)
            } else {
                state.selectedLead.notes[index] = leadNote
            }
        },
        deleteLead(state, action: PayloadAction<number>) {
            const id = action.payload
            state.all = state.all.filter((l) => l.id !== id)
            if (state.selectedLead?.id === id) {
                state.selectedLead = null
            }
        },

        deleteLeadNote(state, action: PayloadAction<LeadNote>) {
            const note = action.payload
            if (!state.selectedLead) {
                return
            }
            state.selectedLead.notes = state.selectedLead.notes.filter((l) => l.id !== note.id)
        },

        deleteLeadTask(state, action: PayloadAction<LeadTask>) {
            const task = action.payload
            if (!state.selectedLead) {
                return
            }
            state.selectedLead.tasks = state.selectedLead.tasks.filter((l) => l.id !== task.id)
        },
        setSelectedLead(state, action: PayloadAction<Lead | null>) {
            state.selectedLead = action.payload ?? null
        },
        clearLeads(state) {
            state.all = []
            state.selectedLead = null
            state.table = {
                ui: { ...initialState.table.ui },
                meta: null,
                hasLoaded: false,
            }
        },
        setLeadsTableUiState(state, action: PayloadAction<Partial<LeadsTableUiState>>) {
            if (!state.table) {
                state.table = {
                    ui: { ...initialState.table.ui },
                    meta: null,
                    hasLoaded: false,
                }
            }
            if (!state.table.ui) {
                state.table.ui = { ...initialState.table.ui }
            }

            // Reset pageIndex when view changes
            if (action.payload.view !== undefined && action.payload.view !== state.table.ui.view) {
                state.table.ui.pageIndex = 0
            }

            state.table.ui = {
                ...state.table.ui,
                ...action.payload,
            }
        },
        resetDefaultLeadsTable(state) {
            if (!state.table) {
                state.table = {
                    ui: { ...initialState.table.ui },
                    meta: null,
                    hasLoaded: false,
                }
                return
            }

            state.table.ui = { ...initialState.table.ui }
        },
        setLeadsTableMeta(state, action: PayloadAction<LeadsTableMetaState>) {
            state.table.meta = action.payload
            state.table.hasLoaded = true
        },
        setLeadTagsCatalog(state, action: PayloadAction<string[]>) {
            const incoming = Array.isArray(action.payload)
                ? action.payload.filter((t) => typeof t === 'string' && t.trim() !== '').map((t) => t.trim())
                : []
            state.tagsCatalog = mergePresetTagsIntoCatalog(incoming)
        },
        addLeadTagsToCatalog(state, action: PayloadAction<string | string[]>) {
            const incoming = (Array.isArray(action.payload) ? action.payload : [action.payload])
                .filter((t): t is string => typeof t === 'string' && t.trim() !== '')
                .map((t) => t.trim())
            if (incoming.length === 0) {
                return
            }

            state.tagsCatalog = mergePresetTagsIntoCatalog([...state.tagsCatalog, ...incoming])
        },
        resetState: () => initialState,
    },
})

export const {
    setLeads,
    pushLead,
    pushLeadTask,
    pushLeadNote,
    deleteLead,
    deleteLeadNote,
    deleteLeadTask,
    setSelectedLead,
    clearLeads,
    setLeadsTableUiState,
    resetDefaultLeadsTable,
    setLeadsTableMeta,
    setLeadTagsCatalog,
    addLeadTagsToCatalog,
    resetState,
} = leadsSlice.actions

export default leadsSlice.reducer
